import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/auth/AuthContext";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { Toaster } from "sonner";
import { usePageView } from "@/components/SEO";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import HowItWorks from "@/pages/HowItWorks";
import Marketplace from "@/pages/Marketplace";
import EventDetail from "@/pages/EventDetail";
import Vendors from "@/pages/Vendors";
import VendorSlugResolver from "@/pages/seo/VendorSlugResolver";
import VendorCategoryCityPage from "@/pages/seo/VendorCategoryCityPage";
import HealthFairsIndex from "@/pages/seo/HealthFairsIndex";
import HealthFairMarketPage from "@/pages/seo/HealthFairMarketPage";

import HostDashboard from "@/pages/HostDashboard";
import HostCreateEvent from "@/pages/HostCreateEvent";
import HostEventDetail from "@/pages/HostEventDetail";

import VendorDashboard from "@/pages/VendorDashboard";
import VendorProfileEdit from "@/pages/VendorProfileEdit";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Inbox from "@/pages/Inbox";
import SmsOptIn from "@/pages/SmsOptIn";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import RefundPolicy from "@/pages/RefundPolicy";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ClaimVendor from "@/pages/ClaimVendor";

import AdminDashboard from "@/pages/AdminDashboard";

import "@/App.css";

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <PageViewTracker />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/sms-opt-in" element={<SmsOptIn />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/claim/:token" element={<ClaimVendor />} />

                        <Route path="/marketplace" element={<Marketplace />} />
                        <Route path="/events/:id" element={<EventDetail />} />
                        <Route path="/vendors" element={<Vendors />} />
                        {/* /vendors/{city}/{category} — highest SEO priority combined page */}
                        <Route path="/vendors/:citySlug/:categorySlug" element={<VendorCategoryCityPage />} />
                        {/* Resolver disambiguates /vendors/{slug} between vendor profile,
                            category landing, city landing, or legacy 24-hex id. */}
                        <Route path="/vendors/:slugOrId" element={<VendorSlugResolver />} />

                        {/* Phase 3: Health fair market pages (event-search intent). */}
                        <Route path="/health-fairs" element={<HealthFairsIndex />} />
                        <Route path="/health-fairs/:slug" element={<HealthFairMarketPage />} />

                        {/* Host */}
                        <Route path="/host" element={<ProtectedRoute roles={["host_company"]}><HostDashboard /></ProtectedRoute>} />
                        <Route path="/host/events/new" element={<ProtectedRoute roles={["host_company"]}><HostCreateEvent /></ProtectedRoute>} />
                        <Route path="/host/events/:id" element={<ProtectedRoute roles={["host_company"]}><HostEventDetail /></ProtectedRoute>} />

                        {/* Vendor */}
                        <Route path="/vendor" element={<ProtectedRoute roles={["vendor"]}><VendorDashboard /></ProtectedRoute>} />
                        <Route path="/vendor/profile" element={<ProtectedRoute roles={["vendor"]}><VendorProfileEdit /></ProtectedRoute>} />
                        <Route path="/vendor/payment-success" element={<ProtectedRoute roles={["vendor"]}><PaymentSuccess /></ProtectedRoute>} />

                        {/* Admin */}
                        <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />

                        {/* Shared inbox (host + vendor) */}
                        <Route path="/inbox" element={<ProtectedRoute roles={["host_company", "vendor"]}><Inbox /></ProtectedRoute>} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

// Lightweight wrapper component so we can call the usePageView hook
// inside <BrowserRouter> context. Tracks SPA route changes server-side.
function PageViewTracker() {
    usePageView();
    return null;
}

export default App;
